import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';
import { Layout } from '../Layout';
import { AppGuard } from './guards/AppGuard';
import { FallbackRedirect } from './guards/FallbackRedirect';
import { Maintenance } from '@domgen/base-design-system-2.0/pages/Maintenance';
import { ClientRoute } from '../models/router/clientRoute';
import { getRoutes } from '../utils/router/getRoutes';
import { FormGuard } from './guards/FormGuard';
import { getJourneyType } from '../utils/store/getJourneyType';

/**
 * @description buildRouter function
 * This function is responsible for building the router configuration for the application.
 *
 * @param clientRoutes - An optional array of client-specific routes that can be added to the router configuration.
 * @param appPath - An optional string representing the base path for the application, used for routing purposes.
 * @param isMaintenanceModeEnabled - A boolean flag indicating whether the application is in maintenance mode. If true, all routes will redirect to a maintenance page.
 * @param brand - A string representing the brand name, used for display purposes on the maintenance page.
 * @param journeyType - A string that can be either 'control' or 'test', determining which set of routes to use for the application.
 *
 * @returns An object containing the configured router and a function to get a route based on a given pathname.
 *
 * The function uses React Router's `createBrowserRouter` and `createRoutesFromElements` to define the routing structure of the application. It conditionally renders different sets of routes based on the `journeyType` and whether maintenance mode is enabled. If maintenance mode is enabled, it renders a single route that displays a maintenance page. Otherwise, it sets up the application routes with guards for authentication and form validation, and includes a fallback redirect for any undefined routes.
 */
export const buildRouter = (
  clientRoutes: ClientRoute[] | undefined = [],
  appPath: string | undefined,
  isMaintenanceModeEnabled: boolean,
  environment?: string,
) => {
  const journeyType = getJourneyType.get();
  const landingHeroPath = `${appPath?.replace(/\/$/, '')}/images/landing-hero.png`;
  const landingBrandPath = `${appPath?.replace(/\/$/, '')}/images/landing-brand.png`;

  const { formStepRoutes: guardRoutes, additionalRoutes } = getRoutes(
    journeyType,
    clientRoutes,
    landingHeroPath,
    landingBrandPath,
    environment,
  );
  const allRoutes = [...guardRoutes, ...additionalRoutes];
  const getRouteFromPath = (pathname: string) => allRoutes.find((route) => route.path === pathname);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Layout />}>
        {isMaintenanceModeEnabled ? (
          <>
            <Route
              path="*"
              element={
                <Maintenance
                  tryAgainLaterText="We apologize for any inconvenience. Please try again after 8am (Eastern Time) on Monday."
                  // phoneNumber="+1 833-552-2314"
                />
              }
            />
          </>
        ) : (
          <Route element={<AppGuard />}>
            <Route element={<FormGuard />}>
              {guardRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
            </Route>
            {additionalRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
            <Route path="*" element={<FallbackRedirect />} />
          </Route>
        )}
      </Route>,
    ),
    { basename: appPath },
  );

  return { router, getRouteFromPath };
};
